export type ThresholdFieldType = 'int' | 'pct'

// ── per-pattern explainer texts ───────────────────────────────────────────────

export interface PatternExplainer {
  /** One-sentence description of what the rule looks for. */
  detects: string
  /**
   * When the rule fires, expressed in plain words.
   * Tokens like {request_count} are substituted from the row's live threshold
   * values at render time via formatExplainer().
   */
  fires: string
  /** Operational meaning + tuning advice for HR (1–2 sentences). */
  advice: string
}

export const PATTERN_EXPLAINERS: Record<string, PatternExplainer> = {
  frequent_sick_leave: {
    detects: 'How often an employee files medical leave in a short window.',
    fires:
      'When an employee files {request_count} or more medical-leave requests in the last {window_days} days. Counts requests, not days.',
    advice:
      'Treated as a wellness signal, not a performance one. A private check-in usually surfaces workload or health pressures behind the pattern.',
  },
  poor_planning_emergency: {
    detects:
      'Employees who repeatedly take emergency leave (same-day, no advance notice) over the year.',
    fires:
      'When an employee logs {request_count} or more emergency leave requests in the last {window_days} days.',
    advice:
      'Flagged for performance review (separate from sick-leave wellness). This is about planning quality — was the leave foreseeable? Tune the threshold upward in teams with genuinely volatile workloads.',
  },
  emergency_approaching_limit: {
    detects:
      'Early warning before "Poor planning (emergency)" fires — the same signal at a lower count.',
    fires:
      'When an employee logs {request_count} or more emergency leave requests in the last {window_days} days. Suppressed automatically when the Poor planning rule also fires (no double-flagging).',
    advice:
      'Heads-up only. Lets a manager have a gentle conversation before the higher threshold triggers a perf-review flag.',
  },
  unpaid_leave_taken: {
    detects: 'Any unpaid leave being taken this year.',
    fires:
      'When the employee has at least one approved unpaid-leave day in the last {window_days} days.',
    advice:
      'Unpaid leave usually means a paid entitlement was exhausted. Flagged for perf review because it indicates either a planning gap or a personal situation HR should be aware of.',
  },
  frequent_half_days: {
    detects:
      'A cluster of half-day leaves in a short window — may mask a longer underlying absence pattern.',
    fires:
      'When an employee files {request_count} or more half-day leaves in the last {window_days} days.',
    advice:
      'Not by itself a performance issue. A check-in helps spot whether a full break would serve the employee better.',
  },
  long_weekend_pattern: {
    detects: 'Repeated leave on Mondays or Fridays — bridging weekends.',
    fires:
      'When an employee has {mon_fri_count} or more approved leaves starting on a Monday or Friday in the last {window_days} days.',
    advice:
      'Wellbeing/awareness signal. Reasonable behaviour individually, but useful to spot if a whole team concentrates leave on the same days and creates coverage gaps.',
  },
  no_leave_in_window: {
    detects:
      'Employees who have taken no approved leave for an extended period — potential burnout risk.',
    fires:
      'When an employee has zero approved leave in the last {window_days} days. New hires (tenure under the window) are excluded automatically.',
    advice:
      'Awareness only — no nudge. Some people simply prefer banked leave. HR can use this to spot consistent non-takers across cycles.',
  },
  high_early_utilisation: {
    detects: 'Employees who burn through annual entitlement in the first half of the year.',
    fires:
      'When an employee has used at least {share_pct}% of annual entitlement before the end of month {cutoff_month}.',
    advice:
      "Planning advisory — HOD may want to anticipate H2 coverage gaps. Not a performance issue; calendar usage is the employee's right.",
  },
}

/**
 * Substitute `{key}` tokens in an explainer text string with live threshold
 * values. Non-numeric values (booleans) are converted to strings. Unknown
 * tokens are left as-is so they remain human-readable.
 *
 * @example
 * formatExplainer('Files {request_count} in {window_days} days', { request_count: 5, window_days: 60 })
 * // → 'Files 5 in 60 days'
 */
export function formatExplainer(
  text: string,
  threshold: Record<string, number | boolean>,
): string {
  return text.replace(/\{([^}]+)\}/g, (match, key: string) => {
    const val = threshold[key]
    if (val === undefined) return match
    return String(val)
  })
}

export interface ThresholdField {
  key: string
  label: string
  type: ThresholdFieldType
  min: number
  max: number
  step?: number
  suffix?: string
}

export const PATTERN_THRESHOLD_SCHEMAS: Record<string, ThresholdField[]> = {
  frequent_sick_leave: [
    { key: 'request_count', label: 'Requests', type: 'int', min: 1, max: 50 },
    { key: 'window_days', label: 'In days', type: 'int', min: 7, max: 365, suffix: 'days' },
  ],
  poor_planning_emergency: [
    { key: 'request_count', label: 'Requests', type: 'int', min: 1, max: 50 },
    { key: 'window_days', label: 'In days', type: 'int', min: 7, max: 365, suffix: 'days' },
  ],
  emergency_approaching_limit: [
    { key: 'request_count', label: 'Requests', type: 'int', min: 1, max: 50 },
    { key: 'window_days', label: 'In days', type: 'int', min: 7, max: 365, suffix: 'days' },
  ],
  unpaid_leave_taken: [
    { key: 'window_days', label: 'In days', type: 'int', min: 7, max: 365, suffix: 'days' },
  ],
  frequent_half_days: [
    { key: 'request_count', label: 'Requests', type: 'int', min: 1, max: 50 },
    { key: 'window_days', label: 'In days', type: 'int', min: 7, max: 365, suffix: 'days' },
  ],
  long_weekend_pattern: [
    { key: 'mon_fri_count', label: 'Mon/Fri leaves', type: 'int', min: 1, max: 50 },
    { key: 'window_days', label: 'In days', type: 'int', min: 7, max: 365, suffix: 'days' },
  ],
  no_leave_in_window: [
    {
      key: 'window_days',
      label: 'Days without leave',
      type: 'int',
      min: 7,
      max: 365,
      suffix: 'days',
    },
  ],
  high_early_utilisation: [
    { key: 'share_pct', label: 'Share used', type: 'pct', min: 10, max: 100, suffix: '%' },
    { key: 'cutoff_month', label: 'By month', type: 'int', min: 1, max: 12 },
  ],
}

export const PATTERN_CODES = Object.keys(PATTERN_THRESHOLD_SCHEMAS)

// ── Per-pattern employee remediation copy ─────────────────────────────────────
// Policy v1.0 §2.2 "What employees see": the system provides plain-language
// guidance on what the flag means AND what the employee can do to address it.
// Keys match PATTERN_CODES exactly. No thresholds here — only guidance copy.

export const PATTERN_REMEDIATION: Record<string, string> = {
  frequent_sick_leave:
    "Don't just push through ok — if it keeps coming back, better get it checked out properly.",
  poor_planning_emergency:
    'If you know the date already, just book it in advance lah — save emergency leave for when you actually need it.',
  emergency_approaching_limit:
    "Got any upcoming days off in mind? Book them now ya, don't be last minute.",
  unpaid_leave_taken: "Don't let your leave balance run out before the year does.",
  frequent_half_days:
    'If recurring half-days point to a scheduling clash or a recurring personal commitment, raise it with your manager — a flexible arrangement may work better for both sides.',
  long_weekend_pattern:
    'Just be mindful if your team needs the coverage on those long weekends.',
  no_leave_in_window:
    "You haven't taken any leave in a while. Consider booking some rest — a break helps you recharge and avoids a build-up that's harder to plan around later.",
  high_early_utilisation:
    'Second half of the year can surprise you — better keep a few days in reserve just in case.',
}

// ── Per-pattern collapsed "personal data point" templates ─────────────────────
// Policy §2.3 health card, collapsed state: instead of a generic definition,
// each card shows a live number specific to the employee, substituted at
// render time from the health-bar endpoint's `patternMetrics[code]` (plus a
// client-supplied `{month}` and `{period}`, the window wording from
// `pattern-window.ts`). Tokens match the backend metric keys exactly
// (see domain/leave_pattern_preview.compute_collapsed_metrics). Patterns
// without a template fall back to PATTERN_EXPLAINERS[code].detects.
export const PATTERN_COLLAPSED_TEMPLATES: Record<string, string> = {
  frequent_sick_leave: "You've had {sick_days} sick days {period}.",
  poor_planning_emergency:
    '{short_notice_pct}% of your leave requests this year were short-notice.',
  emergency_approaching_limit:
    "You're at {emergency_count} emergency leaves this year — one more triggers a flag.",
  unpaid_leave_taken: 'You took {unpaid_days} days of unpaid leave this year.',
  long_weekend_pattern:
    '{mon_fri_days} of your last {total_leave_days} leave days started on a Monday or Friday.',
  high_early_utilisation:
    "You've used {used_days} of {entitlement} annual days — and it's only {month}.",
}

/**
 * Substitute `{key}` tokens in a collapsed-card template with live values.
 * Like {@link formatExplainer} but accepts string values too (for `{month}`).
 * Unknown tokens are left in place so the copy stays legible if a value is
 * missing.
 */
export function formatCollapsed(
  text: string,
  vars: Record<string, string | number>,
): string {
  return text.replace(/\{([^}]+)\}/g, (match, key: string) => {
    const val = vars[key]
    return val === undefined ? match : String(val)
  })
}

/**
 * Prose template per pattern code. Placeholders are `{key}` where `key` matches
 * a field key in PATTERN_THRESHOLD_SCHEMAS.  Rendered by PatternCatalogRow to
 * produce compact inline descriptions like "[4] req in [60] days".
 */
export const PATTERN_THRESHOLD_TEMPLATES: Record<string, string> = {
  frequent_sick_leave: '{request_count} req in {window_days} days',
  poor_planning_emergency: '{request_count} req in {window_days} days',
  emergency_approaching_limit: '{request_count} req in {window_days} days',
  unpaid_leave_taken: 'any in {window_days} days',
  frequent_half_days: '{request_count} req in {window_days} days',
  long_weekend_pattern: '{mon_fri_count} Mon/Fri in {window_days} days',
  no_leave_in_window: '{window_days} days w/o leave',
  high_early_utilisation: '{share_pct}% by month {cutoff_month}',
}
